import type { CrossCheckClause } from '../types/crossCheck';
import apiService from '../services/api';
import { DocumentReader } from '../word/api/DocumentReader';
import { RedlineInserter } from '../word/trackChanges/RedlineInserter';
import { TrackChangesParser } from '../word/utils/TrackChangesParser';
import { getClientRequestXml } from './crossCheckDocumentUtils';

export function clauseHasPendingClientRequestTrackChanges(clause: CrossCheckClause): boolean {
  if (clause.submission_status?.response_submitted) return false;
  const paragraphIndex = clause.paragraph_index;
  if (paragraphIndex == null || Number.isNaN(Number(paragraphIndex))) return false;
  const xml = getClientRequestXml(clause);
  if (!xml) return false;
  try {
    return TrackChangesParser.hasTrackChangesMarkup(xml);
  } catch {
    return false;
  }
}

export function partitionClosableByTrackChanges(clauses: CrossCheckClause[]): {
  withTrackChanges: CrossCheckClause[];
  withoutTrackChanges: CrossCheckClause[];
} {
  const withTrackChanges: CrossCheckClause[] = [];
  const withoutTrackChanges: CrossCheckClause[] = [];
  for (const clause of clauses) {
    if (clauseHasPendingClientRequestTrackChanges(clause)) {
      withTrackChanges.push(clause);
    } else {
      withoutTrackChanges.push(clause);
    }
  }
  return { withTrackChanges, withoutTrackChanges };
}

export async function acceptClientRequestTrackChangesForClause(
  subProjectId: string,
  clause: CrossCheckClause,
): Promise<boolean> {
  const paragraphIndex = clause.paragraph_index;
  const sourceXml = getClientRequestXml(clause);
  if (
    !sourceXml ||
    !TrackChangesParser.hasTrackChangesMarkup(sourceXml) ||
    paragraphIndex == null ||
    Number.isNaN(Number(paragraphIndex))
  ) {
    return false;
  }

  await DocumentReader.navigateToParagraph(Number(paragraphIndex));
  const { acceptedCount, revertContext } = await RedlineInserter.acceptTrackedChangesInParagraph(
    Number(paragraphIndex),
  );

  if (acceptedCount === 0) {
    return false;
  }

  const response = await apiService.updateClauseResponseSubmissionStatus(
    subProjectId,
    clause.id,
    true,
    revertContext,
  );
  return Boolean(response?.success);
}

export async function acceptClausesSequentially(
  subProjectId: string,
  clauses: CrossCheckClause[],
  onProgress?: (current: number, total: number) => void,
): Promise<string[]> {
  const acceptedIds: string[] = [];
  const total = clauses.length;
  for (let index = 0; index < total; index += 1) {
    const clause = clauses[index];
    onProgress?.(index + 1, total);
    const accepted = await acceptClientRequestTrackChangesForClause(subProjectId, clause);
    if (accepted) {
      acceptedIds.push(clause.id);
    }
  }
  return acceptedIds;
}
